import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import instructorManagementApi from '../../imt/api/instructorManagementApi';
import { convertTo24HourFormat } from '../components/Activity/Create/ActivityCreateForm';
import { RecommendedInstructorProfileData } from '../interfaces/recommendedInstructorProfile';
import { LookupData } from '../interfaces/lookup';
import {
    BlockedTime,
    BlockedTimeFormData,
    BlockedTimeTimestamp,
    BlockTimeTimeAttributeEditorItem,
} from '../../common/interfaces/blockedTime';
import { MeridiemFieldValue } from '../interfaces/meridiem';
import { DeliverySession } from '../interfaces/activity';
import { InstructorEventTypes } from '../interfaces/resourceCalendarProps';
import { CALENDAR_EVENT_COLOR } from '../constants';
import {
    convertTimeStampToLocalUnix,
    formatUnixTimeInTimezone,
    getUnixTimeInTimezoneFromDatestring,
} from '../../common/utils/date-time.utils';

dayjs.extend(utc);
dayjs.extend(timezone);

const formatBlockedTimeAttributes = ({
    blockedTime,
    timezone,
}: {
    blockedTime: BlockTimeTimeAttributeEditorItem;
    timezone: string | undefined;
}) => {
    if (!timezone) {
        timezone = dayjs.tz.guess();
    }

    let endTimestamp = convertTo24HourFormat(
        blockedTime.endTime,
        blockedTime.endTimeMeridiemFieldOption,
    );

    let startTimestamp = convertTo24HourFormat(
        blockedTime.startTime,
        blockedTime.startTimeMeridiemFieldOption,
    );
    return {
        end_timestamp: getUnixTimeInTimezoneFromDatestring({
            datestring: `${blockedTime.endDateString} ${endTimestamp}`,
            timezone,
        }),
        start_timestamp: getUnixTimeInTimezoneFromDatestring({
            datestring: `${blockedTime.startDateString} ${startTimestamp}`,
            timezone,
        }),
    };
};

export const formatFreelancerBlockedTimeAttributes = ({
    id,
    blockedTime,
    timezone,
    forceDelete,
}: {
    id: string;
    blockedTime: BlockTimeTimeAttributeEditorItem;
    timezone: string;
    forceDelete: boolean;
}) => {
    const endTimeDateString = `${blockedTime.endDateString} 23:59:59`;
    const startTimeDateString = `${blockedTime.startDateString} 00:00:00`;

    return {
        id,
        end_timestamp: getUnixTimeInTimezoneFromDatestring({
            datestring: endTimeDateString,
            timezone,
        }),
        start_timestamp: getUnixTimeInTimezoneFromDatestring({
            datestring: startTimeDateString,
            timezone,
        }),
        removeFlag: forceDelete ? forceDelete : blockedTime.removeFlag,
    };
};

export const normalizeBlockedTimeAttributes = (
    instructor: RecommendedInstructorProfileData,
    blockedTimeNormalizedData: BlockedTimeFormData,
) => {
    const blockTime = {
        blocked_time_type: blockedTimeNormalizedData.blocked_time_type,
        blocked_time_name: blockedTimeNormalizedData.blocked_time_name,
        blocked_time_notes: blockedTimeNormalizedData.blocked_time_notes,
        pk: blockedTimeNormalizedData.pk,
    };
    const timezone = instructor.city_timezone;
    const timestamps = blockedTimeNormalizedData.blockedTimes.map(
        (blockedTime) => {
            return formatBlockedTimeAttributes({ blockedTime, timezone });
        },
    );
    return {
        instructor_pk: instructor.pk,
        ...blockTime,
        blocked_time_timestamps: timestamps,
    };
};

interface CalendarUpdateEvents {
    blockedTimeData: BlockedTime;
}

export const normalizeInstructorsWithBlockedTimeTimeData = ({
    selectedInstructors,
    blockedTimeFormValues,
    instructorLookup,
}: {
    selectedInstructors: Array<RecommendedInstructorProfileData>;
    blockedTimeFormValues: BlockedTimeFormData;
    instructorLookup: LookupData<RecommendedInstructorProfileData>;
}) =>
    Array.from(selectedInstructors).reduce(
        (acc, instructor) => {
            if (instructor.pk && instructorLookup[instructor.pk]) {
                let blockedTimeData = normalizeBlockedTimeAttributes(
                    instructor,
                    blockedTimeFormValues,
                );
                acc.blockedTimes.push(blockedTimeData);
                acc.calendarEventsLookup[instructor.pk as string] = {
                    blockedTimeData,
                };
            }
            return acc;
        },
        {
            blockedTimes: [] as Array<BlockedTime>,
            calendarEventsLookup: {} as LookupData<CalendarUpdateEvents>,
        },
    );

export const buildEditTimesForInstructorType = ({
    timestamp,
    city_timezone,
    blockedTime,
    isFreelancer,
}: {
    timestamp: BlockedTimeTimestamp;
    city_timezone: string;
    blockedTime: BlockedTime;
    isFreelancer: boolean;
}) => {
    let editTimes = {
        endTimeMeridiemFieldOption: MeridiemFieldValue.Pm,
        startTimeMeridiemFieldOption: MeridiemFieldValue.Am,
        id: blockedTime.pk,
    } as unknown as BlockTimeTimeAttributeEditorItem;

    editTimes = {
        ...editTimes,
        startTime: formatUnixTimeInTimezone({
            timestamp: timestamp.start_timestamp,
            timezone: city_timezone,
            format: 'hh:mm',
        }),
        endTime: formatUnixTimeInTimezone({
            timestamp: timestamp.end_timestamp,
            timezone: city_timezone,
            format: 'hh:mm',
        }),
        startDateString: formatUnixTimeInTimezone({
            timestamp: timestamp.start_timestamp,
            timezone: city_timezone,
            format: 'YYYY-MM-DD',
        }),
        endDateString: formatUnixTimeInTimezone({
            timestamp: timestamp.end_timestamp,
            timezone: city_timezone,
            format: 'YYYY-MM-DD',
        }),
    };

    if (isFreelancer) {
        editTimes = {
            ...editTimes,
            removeFlag: false,
        };
    }

    return editTimes;
};

export const buildCalendarEventsFromDeliverySessions = (
    {
        sessions,
        timezone,
    }: {
        sessions: Array<DeliverySession>;
        timezone: string;
    },
    getEventObj: any,
) => {
    let events: Array<any> = [];

    let pendingSession: DeliverySession | null = null;
    let startingSession: DeliverySession | null = null;
    // if the event has more than one session and the dates are not back to back
    // we want a separate event for each session
    for (let sessionIndex in sessions) {
        let deliverySession: DeliverySession = sessions[sessionIndex];
        let shouldPushEvent: boolean = false;

        if (!pendingSession) {
            pendingSession = deliverySession;
        }

        if (!startingSession) {
            startingSession = deliverySession;
        }

        // see if this session start is not within 24h of the pending session start, if not
        // we should push this event
        if (
            deliverySession.start_timestamp &&
            Math.abs(
                ((pendingSession.start_timestamp as number) -
                    deliverySession.start_timestamp) as number,
            ) >
                60 * 60 * 24
        ) {
            shouldPushEvent = true;
        }
        if (pendingSession && startingSession && shouldPushEvent) {
            events.push({
                ...getEventObj(),
                ...{
                    start:
                        convertTimeStampToLocalUnix({
                            timestamp:
                                startingSession.start_timestamp as number,
                            timezone: timezone,
                        }) * 1000,
                    end:
                        convertTimeStampToLocalUnix({
                            timestamp: pendingSession.end_timestamp as number,
                            timezone: timezone,
                        }) * 1000,
                    backgroundColor: CALENDAR_EVENT_COLOR,
                    eventType: InstructorEventTypes.activity,
                },
            });

            startingSession = deliverySession;
        }

        if (sessions.length === parseInt(sessionIndex) + 1) {
            // this is the last loop, if we have no starting session, it means a one off outlier
            events.push({
                ...getEventObj(sessionIndex),
                ...{
                    start:
                        convertTimeStampToLocalUnix({
                            timestamp:
                                startingSession.start_timestamp as number,
                            timezone: timezone,
                        }) * 1000,
                    end:
                        convertTimeStampToLocalUnix({
                            timestamp: deliverySession.end_timestamp as number,
                            timezone: timezone,
                        }) * 1000,
                    backgroundColor: CALENDAR_EVENT_COLOR,
                    eventType: InstructorEventTypes.activity,
                },
            });
        }

        pendingSession = deliverySession;
    }

    return events;
};
